import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paginator } from 'primereact/paginator';
import { useDebounce } from 'use-debounce';
import { apiErrorMessage, candidatePath, formatDate, useCareersRequest } from './careersShared';
import {
  CareersPage, EmptyState, ErrorBanner, Loading, PageHeader, PersonAvatar, SearchBox,
} from './careersUI';
import './careers.css';

const PAGE_SIZE = 25;

const ACTIVE_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Deactivated', value: 'false' },
];

const Candidates = () => {
  const request = useCareersRequest();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 350);
  const [active, setActive] = useState('all');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await request('/candidates', {
        params: {
          search: debouncedSearch || undefined,
          active: active === 'all' ? undefined : active,
          page,
          pageSize: PAGE_SIZE,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Could not load candidates.'));
    } finally {
      setLoading(false);
    }
  }, [request, debouncedSearch, active, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [debouncedSearch, active]);

  return (
    <CareersPage>
      <PageHeader title="Candidates" subtitle="People who created an account on the careers portal." />

      <div className="careers-card is-flush">
        <div className="careers-toolbar">
          <div className="careers-tabs" role="group" aria-label="Filter by account state">
            {ACTIVE_FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={active === option.value ? 'is-active' : ''}
                aria-pressed={active === option.value}
                onClick={() => setActive(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder="Search name, email or phone" label="Search candidates" />
        </div>

        <ErrorBanner message={loadError} onRetry={load} />
        {loading && !items.length ? (
          <Loading />
        ) : !items.length && !loadError ? (
          <EmptyState icon="pi-users" title="No candidates found" />
        ) : (
          <div className="careers-table-wrap">
            <table className="careers-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Title / location</th>
                  <th>Applications</th>
                  <th>Account</th>
                  <th>Joined</th>
                  <th>Last sign-in</th>
                </tr>
              </thead>
              <tbody>
                {items.map((candidate) => (
                  <tr key={candidate.candidate_id} className="careers-clickable-row" onClick={() => navigate(candidatePath(candidate.candidate_id))}>
                    <td style={{ minWidth: 240 }}>
                      <div className="careers-person">
                        <PersonAvatar name={candidate.name} />
                        <div className="text-truncate">
                          <span className="careers-primary-text">{candidate.name}</span>
                          <span className="careers-meta text-truncate">{candidate.email}{candidate.phone ? ` · ${candidate.phone}` : ''}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {candidate.title || '—'}
                      <span className="careers-meta">{candidate.location || ''}</span>
                    </td>
                    <td className="is-num"><span className="careers-count-link">{candidate.application_count}</span></td>
                    <td>
                      {candidate.is_active
                        ? <span className="careers-badge is-ok">Active</span>
                        : <span className="careers-badge is-off">Deactivated</span>}
                      {!candidate.email_verified && <span className="careers-meta">Email not verified</span>}
                    </td>
                    <td className="is-num">{formatDate(candidate.createdAt)}</td>
                    <td className="is-num">{formatDate(candidate.last_login_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div className="careers-table-footer">
            <span>{total} {total === 1 ? 'candidate' : 'candidates'}</span>
            {total > PAGE_SIZE && (
              <Paginator
                first={(page - 1) * PAGE_SIZE}
                rows={PAGE_SIZE}
                totalRecords={total}
                onPageChange={(e) => setPage(e.page + 1)}
              />
            )}
          </div>
        )}
      </div>
    </CareersPage>
  );
};

export default Candidates;
